import { isComputerMenuActionAllowed } from '#pplx/frontend/apps/ask/ask-input/ask-input-computer-command-menu/computerCommandPolicy.js';
import { pluginToMenuItem } from '#pplx/frontend/apps/ask/ask-input/ask-input-computer-command-menu/plugins/pluginToMenuItem.js';
import { getSkillDisplayDescription, getSkillDisplayName, } from '#pplx/frontend/apps/ask/skill-cards/skillDisplay.js';
import { resolveLocalizedSkillName } from '#pplx/frontend/apps/ask/skills/getComposerSkillMention.js';
import { PLUGIN_ICON } from '#pplx/frontend/apps/ask/skills/utils/getMenuIcon.js';
import { getMenuIcon } from '#pplx/frontend/apps/ask/skills/utils/getMenuIcon.js';
import { isSourceAllowed, isSourceConnected, } from '#pplx/frontend/apps/ask/sources/hooks/sourceStatusPredicates.js';
import { assertUnreachableValue } from '#pplx/frontend/common/type_utils/unreachableValue.js';
const UI_ACTION_SEARCH_ALIASES = {
    switch_to_search_mode: {
        slashCommands: ['/search'],
        naturalLanguage: 'search mode',
    },
    open_computer_settings: {
        slashCommands: ['/settings'],
        naturalLanguage: 'computer settings',
    },
    toggle_incognito: {
        slashCommands: ['/incognito'],
    },
    open_feedback: {
        slashCommands: ['/feedback'],
        naturalLanguage: 'share feedback',
    },
    open_model_selector: {
        slashCommands: ['/model'],
        naturalLanguage: 'model selector',
    },
    open_side_chat: {
        slashCommands: ['/ask', '/side', '/btw'],
    },
    fork_session: {
        slashCommands: ['/fork'],
    },
};
export function buildComputerMenuCatalog({ featuredItems, commandItems, skills, plugins, connectors = [], intl, isIncognito, isFollowUp, isAlwaysIncognitoRequired, hideCommandItems, hideSettingsAction, }) {
    return {
        featuredItems: featuredItems
            .filter((item) => !(hideSettingsAction &&
            item.action.type === 'ui_action' &&
            item.action.action_id === 'open_computer_settings'))
            .map((item) => featuredItemToMenuItem(item, intl))
            .filter((item) => isComputerMenuItemAllowed(item, isFollowUp, isAlwaysIncognitoRequired)),
        commandItems: (hideCommandItems ? [] : commandItems)
            .map((item) => commandItemToMenuItem(item, intl, isIncognito))
            .filter((item) => isComputerMenuItemAllowed(item, isFollowUp, isAlwaysIncognitoRequired)),
        customSkillItems: skills.map((skill) => skillToMenuItem(skill, intl)),
        pluginItems: [...plugins.map(pluginToMenuItem), ...connectors.map(connectorToMenuItem)],
    };
}
export function createComputerMenuPresentation(catalog, { query, serverSearchSource }) {
    const matchingFeaturedItems = catalog.featuredItems.filter((item) => itemMatchesSearchQuery(item, query));
    const matchingCommandItems = catalog.commandItems.filter((item) => itemMatchesSearchQuery(item, query));
    const [exactCommandItems, remainingCommandItems] = partitionExactCommandMatches(matchingCommandItems, query);
    const featuredItems = prioritizeSlashCommandPrefixMatches(matchingFeaturedItems, query);
    const commandItems = [...exactCommandItems, ...remainingCommandItems];
    const filterServerSearched = (items) => serverSearchSource === 'locally-filtered'
        ? items.filter((item) => itemMatchesSearchQuery(item, query))
        : items;
    const customSkillItems = filterServerSearched(catalog.customSkillItems);
    const pluginItems = catalog.pluginItems.filter((item) => item.action.type === 'connector' || serverSearchSource === 'locally-filtered'
        ? itemMatchesSearchQuery(item, query)
        : true);
    const commandItemsFirst = exactCommandItems.length > 0;
    const orderedItems = commandItemsFirst
        ? [...commandItems, ...pluginItems, ...featuredItems, ...customSkillItems]
        : [...featuredItems, ...pluginItems, ...customSkillItems, ...commandItems];
    return {
        commandItemsFirst,
        featuredItems,
        commandItems,
        customSkillItems,
        pluginItems,
        orderedItems,
    };
}
export function isComputerMenuItemAllowed(item, isFollowUp, isAlwaysIncognitoRequired) {
    return isComputerMenuActionAllowed({
        action: item.action,
        isFollowUp,
        isAlwaysIncognitoRequired,
    });
}
export function getFeaturedItemTitle(featuredItem, intl) {
    if (featuredItem.id === 'create-skill') {
        return intl.formatMessage({
            defaultMessage: 'Create Skill',
            description: 'Button text to create a new skill',
            id: '2e82ef4908',
        });
    }
    return (resolveLocalizedSkillName({ skill: { name: featuredItem.id }, intl }) ?? featuredItem.title);
}
function getUiActionSearchAliasValues(actionId) {
    const aliases = UI_ACTION_SEARCH_ALIASES[actionId];
    if (!aliases)
        return [];
    const { slashCommands, naturalLanguage } = aliases;
    return naturalLanguage ? [...slashCommands, naturalLanguage] : slashCommands;
}
function featuredItemToMenuItem(featuredItem, intl) {
    const { action: featuredItemAction } = featuredItem;
    let action;
    let searchAliases;
    switch (featuredItemAction.type) {
        case 'skill':
            action = {
                type: 'skill',
                selectableSkill: {
                    id: featuredItemAction.skill_id,
                    name: featuredItemAction.skill_name,
                    display_name: getFeaturedItemTitle(featuredItem, intl),
                    description: featuredItem.description,
                    scope: 'global',
                },
            };
            break;
        case 'slash_command':
            action = {
                type: 'slash_command',
                commandName: featuredItemAction.command_name,
                prefix: featuredItemAction.prefix,
                submittedLabel: featuredItemAction.submitted_label,
            };
            break;
        case 'ui_action':
            action = {
                type: 'ui_action',
                actionId: featuredItemAction.action_id,
            };
            searchAliases = getUiActionSearchAliasValues(featuredItemAction.action_id);
            break;
        default:
            return assertUnreachableValue(featuredItemAction);
    }
    return {
        type: 'featured',
        id: featuredItem.id,
        title: getFeaturedItemTitle(featuredItem, intl),
        description: featuredItem.description,
        icon: getMenuIcon(featuredItem.icon),
        isNew: Boolean(featuredItem.is_new),
        heroImageUrl: featuredItem.hero_image_url,
        searchAliases,
        action,
    };
}
function commandItemToMenuItem(commandItem, intl, isIncognito) {
    const { action } = commandItem;
    switch (action.type) {
        case 'ui_action': {
            const isExitIncognito = action.action_id === 'toggle_incognito' && isIncognito;
            return {
                type: 'command',
                id: commandItem.id,
                title: isExitIncognito
                    ? intl.formatMessage({
                        defaultMessage: 'Exit incognito',
                        id: 'aa527181c9',
                        description: 'Computer command menu item shown when selecting the incognito command will turn incognito off',
                    })
                    : commandItem.title,
                description: commandItem.description,
                icon: getMenuIcon(commandItem.icon),
                isNew: Boolean(commandItem.is_new),
                heroImageUrl: commandItem.hero_image_url,
                searchAliases: getUiActionSearchAliasValues(action.action_id),
                action: {
                    type: 'ui_action',
                    actionId: action.action_id,
                },
            };
        }
        case 'slash_command':
            return {
                type: 'command',
                id: commandItem.id,
                title: commandItem.title,
                description: commandItem.description,
                icon: getMenuIcon(commandItem.icon),
                isNew: Boolean(commandItem.is_new),
                heroImageUrl: commandItem.hero_image_url,
                action: {
                    type: 'slash_command',
                    commandName: action.command_name,
                    prefix: action.prefix,
                    submittedLabel: action.submitted_label,
                },
            };
        default:
            return assertUnreachableValue(action);
    }
}
function getSkillSourceLabel(skill, intl) {
    if (skill.scope === 'organization') {
        return intl.formatMessage({
            defaultMessage: 'Organization skill',
            description: 'Label shown under a skill title when the skill belongs to an organization',
            id: 'a85adb727f',
        });
    }
    if (skill.scope === 'individual') {
        return intl.formatMessage({
            defaultMessage: 'Created by you',
            description: 'Label shown under a skill title when the skill was created by the current user',
            id: '16d36f6d61',
        });
    }
    return undefined;
}
function skillToMenuItem(skill, intl) {
    return {
        type: 'skill',
        id: skill.id,
        title: getSkillDisplayName(skill),
        description: getSkillDisplayDescription(skill),
        icon: getMenuIcon(skill.name),
        isNew: false,
        sourceLabel: getSkillSourceLabel(skill, intl),
        heroImageUrl: null,
        action: {
            type: 'skill',
            selectableSkill: skill,
        },
    };
}
function getActionSearchText(action) {
    switch (action.type) {
        case 'slash_command':
            return [action.commandName, action.prefix];
        case 'connector':
            return [action.source.id, action.source.display_name ?? ''];
        case 'plugin':
            return [action.plugin.action.plugin_name];
        case 'ui_action':
            return [action.actionId];
        case 'skill':
            return [action.selectableSkill.name, action.selectableSkill.scope];
        default:
            return assertUnreachableValue(action);
    }
}
function getItemSearchText(item) {
    return [
        item.id,
        item.title,
        item.description,
        ...(item.searchAliases ?? []),
        ...getActionSearchText(item.action),
    ];
}
function itemMatchesSearchQuery(item, query) {
    if (!query)
        return true;
    if (isSideChatCommandQuery(item, query))
        return true;
    return getItemSearchText(item).some((value) => value?.toLocaleLowerCase().includes(query));
}
function isSideChatCommandQuery(item, query) {
    if (item.action.type !== 'ui_action' || item.action.actionId !== 'open_side_chat')
        return false;
    const normalizedQuery = query.startsWith('/') ? query : `/${query}`;
    return (item.searchAliases?.some((alias) => normalizedQuery.startsWith(`${alias.toLocaleLowerCase()} `)) ?? false);
}
function isExactCommandMatch(item, query) {
    if (!query || item.type !== 'command')
        return false;
    return getItemSearchText(item).some((value) => value?.toLocaleLowerCase() === query);
}
function partitionExactCommandMatches(items, query) {
    if (!query)
        return [[], items];
    return items.reduce((partitionedItems, item) => {
        partitionedItems[isExactCommandMatch(item, query) ? 0 : 1].push(item);
        return partitionedItems;
    }, [[], []]);
}
function getSlashCommandNameSearchQuery(query) {
    return query.startsWith('/') ? query.slice(1) : query;
}
function isSlashCommandPrefixMatch(item, query) {
    if (!query)
        return false;
    const commandNameSearchQuery = getSlashCommandNameSearchQuery(query);
    if (!commandNameSearchQuery)
        return false;
    const slashCommands = item.action.type === 'slash_command'
        ? [item.action.prefix]
        : item.searchAliases?.filter((alias) => alias.startsWith('/'));
    return (slashCommands?.some((slashCommand) => slashCommand.slice(1).toLocaleLowerCase().startsWith(commandNameSearchQuery)) ?? false);
}
function prioritizeSlashCommandPrefixMatches(items, query) {
    if (!query)
        return items;
    const [commandPrefixMatches, otherItems] = items.reduce((partitionedItems, item) => {
        partitionedItems[isSlashCommandPrefixMatch(item, query) ? 0 : 1].push(item);
        return partitionedItems;
    }, [[], []]);
    return [...commandPrefixMatches, ...otherItems];
}
function connectorToMenuItem(source) {
    return {
        type: 'plugin',
        id: `connector-${source.id}`,
        disabled: !isSourceAllowed(source) || !isSourceConnected(source),
        title: source.display_name || source.id,
        description: source.description ?? '',
        icon: PLUGIN_ICON,
        isNew: false,
        sourceLabel: undefined,
        heroImageUrl: null,
        action: { type: 'connector', source },
    };
}
//# sourceMappingURL=computerMenuModel.js.map